import { useState } from 'react';
import { enqueueSnackbar } from 'notistack';
// @mui
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
//
import axios, { endpoints, formatEndpoint } from 'src/utils/axios';
import { BackendValidationError } from 'src/utils/backend-validation-error';
import { FUEL_PROVIDERS, IftaFiling, MILES_SOURCES } from 'src/models';
//
import FileDrop from './file-drop';
import SourcePicker from './source-picker';
import MilesInstructions from './miles-instructions';

// ----------------------------------------------------------------------

type Props = {
  open: boolean;
  kind: 'miles' | 'fuel';
  filing: IftaFiling;
  onClose: () => void;
  onUploaded: (filing: IftaFiling) => void;
};

// Same look as the wizard itself: step 1 shows the big provider cards, step 2
// the provider's export instructions plus the drop zone.
export default function UploadFileDialog({ open, kind, filing, onClose, onUploaded }: Props) {
  const options = kind === 'miles' ? MILES_SOURCES : FUEL_PROVIDERS;
  const [step, setStep] = useState<1 | 2>(1);
  const [source, setSource] = useState('');
  const [uploading, setUploading] = useState(false);

  const reset = () => {
    setStep(1);
    setSource('');
    setUploading(false);
  };

  const close = () => {
    if (uploading) return;
    reset();
    onClose();
  };

  const uploadFile = async (file: File) => {
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('source', source);
      const endpoint = kind === 'miles' ? endpoints.filings.uploadMiles : endpoints.filings.uploadFuel;
      const response = await axios.post(formatEndpoint(endpoint, { id: filing.id }), formData);
      onUploaded(response.data.filing);
      enqueueSnackbar('File added. We recalculate your filing with the new lines.', { variant: 'success' });
      reset();
      onClose();
    } catch (error) {
      const messages: BackendValidationError[] = error?.response?.data;
      if (Array.isArray(messages)) {
        messages.forEach((message) => enqueueSnackbar(message.message, { variant: 'error' }));
      } else {
        enqueueSnackbar('Something went wrong uploading your file', { variant: 'error' });
      }
      setUploading(false);
    }
  };

  return (
    <Dialog open={open} onClose={close} maxWidth="sm" fullWidth>
      <DialogTitle>
        {step === 1
          ? `Where is this ${kind === 'miles' ? 'mileage' : 'fuel'} report from?`
          : 'Upload your file'}
      </DialogTitle>
      <DialogContent>
        {step === 1 ? (
          <Stack sx={{ pt: 1 }}>
            <SourcePicker options={options} value={source} onSelect={setSource} columns={{ xs: 6, sm: 4 }} />
          </Stack>
        ) : (
          <Stack spacing={2.5} sx={{ pt: 1 }}>
            <MilesInstructions source={source} quarter={filing.selectedQuarter} kind={kind} />
            <FileDrop
              uploading={uploading}
              onFile={uploadFile}
              hint={kind === 'miles' ? 'Mileage report (CSV or XLSX)' : 'Fuel transactions (CSV or XLSX)'}
            />
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button color="inherit" onClick={close} disabled={uploading}>
          Cancel
        </Button>
        {step === 1 ? (
          <Button
            variant="contained"
            color="secondary"
            disabled={!source}
            onClick={() => setStep(2)}
            data-testid="upload-dialog-next"
          >
            Next
          </Button>
        ) : (
          <Button color="inherit" onClick={() => setStep(1)} disabled={uploading}>
            Back
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
